/**
 * StudySync Fitbit OAuth Callback Component
 * Handles Fitbit OAuth callback and token exchange
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import { Container, Paper, Typography, Box, Alert } from "@mui/material";
import {
  CheckCircle,
  Error as ErrorIcon,
  FitnessCenter,
} from "@mui/icons-material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { fitbitConfig } from "../../utils/fitbitConfig";
import LoadingSpinner from "../common/LoadingSpinner";
import { analyticsService } from "../../services/analyticsService";

const FitbitCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [error, setError] = useState("");
  const [reauthorized, setReauthorized] = useState(false);
  const hasSetSuccess = useRef(false);
  const hasProcessed = useRef(false);

  const handleCallback = useCallback(async () => {
    // Prevent duplicate processing using useRef for SYNCHRONOUS checking
    if (hasProcessed.current) {
      return;
    }

    // SET FLAG IMMEDIATELY (synchronously) before any async operations to prevent race conditions
    hasProcessed.current = true;

    try {
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const error = searchParams.get("error");

      if (error) {
        throw new globalThis.Error(`Fitbit authorization failed: ${error}`);
      }

      if (!code || !state) {
        throw new globalThis.Error(
          "Missing authorization code or state parameter"
        );
      }

      // Parse state: backend uses "participantId:studyCode:timestamp"; frontend PKCE uses base64 JSON
      let stateData: { studyCode?: string; participantId?: string; type?: string; codeVerifier?: string };
      let isBackendState = false;
      try {
        const decoded = decodeURIComponent(state);
        try {
          stateData = JSON.parse(atob(decoded));
        } catch {
          const parts = decoded.split(":");
          if (parts.length >= 2) {
            stateData = { participantId: parts[0], studyCode: parts[1] };
            isBackendState = true;
          } else {
            throw new globalThis.Error("Invalid state parameter");
          }
        }
      } catch (e: any) {
        throw e?.message ? e : new globalThis.Error("Invalid state parameter");
      }

      const {
        studyCode,
        participantId,
        codeVerifier: stateCodeVerifier,
      } = stateData;

      if (!studyCode) {
        throw new globalThis.Error("Missing study code in state");
      }

      // Get participantId from state or localStorage
      const finalParticipantId =
        participantId ||
        (() => {
          try {
            const storedData = localStorage.getItem("fitbit_participant_data");
            if (storedData) {
              const parsed = JSON.parse(storedData);
              return parsed.participantId;
            }
          } catch (e) {}
          return null;
        })();

      if (!finalParticipantId) {
        throw new globalThis.Error(
          "Missing participant ID. Please restart the enrollment process."
        );
      }

      // Get code verifier from state parameter or localStorage (PKCE flow)
      const codeVerifier =
        stateCodeVerifier || localStorage.getItem("fitbit_code_verifier");
      const storedStudyCode = localStorage.getItem("fitbit_study_code");

      // Backend flow: no PKCE (direct_enrollment or re-auth link with colon state).
      // Prefer the backend callback path whenever the state came from the backend,
      // even if a stale code_verifier is still present in localStorage.
      if ((isBackendState || !codeVerifier) && finalParticipantId && studyCode) {
        const callbackResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/fitbit/callback`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              code,
              state,
              participantId: finalParticipantId,
            }),
          }
        );

        if (!callbackResponse.ok) {
          const errorData = await callbackResponse.json();
          throw new globalThis.Error(
            errorData.error || "Failed to process Fitbit authorization"
          );
        }

        const callbackData = await callbackResponse.json();
        if (callbackData.reauthorized) {
          setReauthorized(true);
        }
        // Store web JWT so participant can complete profile (update-info) without "Access token required"
        if (callbackData.accessToken) {
          try {
            sessionStorage.setItem(
              "studysync_participant_web_tokens",
              JSON.stringify({
                accessToken: callbackData.accessToken,
                refreshToken: callbackData.refreshToken || null,
                expiresAt: callbackData.expiresAt || null,
              })
            );
          } catch (e) {
            console.warn("Could not store participant tokens for update-info", e);
          }
        }
        const response = {
          success: true,
          data: {
            participantId: callbackData.participantId || finalParticipantId,
            authorizationStatus: true,
          },
        };

        // Set success state
        hasSetSuccess.current = true;
        setStatus("success");
        analyticsService.trackParticipantFitbitAuthorized({ success: true });

        // Capture any stored participant contact info before cleanup
        let storedEmail: string | undefined;
        let storedPhoneNumber: string | undefined;
        try {
          const stored = localStorage.getItem("fitbit_participant_data");
          if (stored) {
            const parsed = JSON.parse(stored);
            storedEmail = parsed.email;
            storedPhoneNumber = parsed.phoneNumber;
          }
        } catch {}

        // Clean up stored data
        localStorage.removeItem("fitbit_code_verifier");
        localStorage.removeItem("fitbit_study_code");
        localStorage.removeItem("fitbit_participant_data");

        // Send success message to parent window if in popup (include tokens so opener can store them for update-info)
        if (window.opener) {
          window.opener.postMessage(
            {
              type: "FITBIT_OAUTH_SUCCESS",
              fitbitData: response.data,
              ...(callbackData.accessToken && {
                accessToken: callbackData.accessToken,
                refreshToken: callbackData.refreshToken || null,
                expiresAt: callbackData.expiresAt || null,
              }),
            },
            window.location.origin
          );
          window.close();
          return;
        }

        // If re-auth, just show success (no redirect). Otherwise redirect to info collection (new enrollment)
        if (!callbackData.reauthorized) {
          setTimeout(() => {
            navigate("/participant/info", {
              state: {
                participantId: response.data.participantId,
                studyCode: studyCode,
                email: storedEmail,
                phoneNumber: storedPhoneNumber,
                step: 6, // Direct to step 6 (Personal Information)
              },
            });
          }, 3000);
        }
        return;
      }

      // PKCE flow - require code verifier
      if (!codeVerifier) {
        throw new globalThis.Error(
          "Missing PKCE code verifier. OAuth flow may not have been initiated properly."
        );
      }

      // Exchange authorization code for tokens using PKCE flow
      const tokenResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/data/exchange-fitbit-token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            code,
            codeVerifier,
            redirectUri: fitbitConfig.getConfig().redirectUri,
            studyCode: studyCode || storedStudyCode,
          }),
        }
      );

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json();
        throw new globalThis.Error(
          errorData.error || "Failed to exchange authorization code"
        );
      }

      const tokenData = await tokenResponse.json();

      // Store tokens using the same endpoint as working client
      const storeRequestData = {
        accessToken: tokenData.access_token,
        refreshToken: tokenData.refresh_token,
        participantCode: studyCode || storedStudyCode,
        participantId: finalParticipantId, // Pass the specific participant ID
        participantInfo: {
          userId: tokenData.user_id,
          fitbitUserId: tokenData.user_id,
          scope: tokenData.scope,
        },
      };

      const storeResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/data/store-tokens`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(storeRequestData),
        }
      );

      if (!storeResponse.ok) {
        const errorData = await storeResponse.json();
        throw new globalThis.Error(errorData.error || "Failed to store tokens");
      }

      const storeData = await storeResponse.json();
      // Set success state
      hasSetSuccess.current = true;
      setStatus("success");

      // Capture any stored participant contact info before cleanup
      let storedEmail2: string | undefined;
      let storedPhoneNumber2: string | undefined;
      try {
        const stored = localStorage.getItem("fitbit_participant_data");
        if (stored) {
          const parsed = JSON.parse(stored);
          storedEmail2 = parsed.email;
          storedPhoneNumber2 = parsed.phoneNumber;
        }
      } catch {}

      // Clean up stored data
      localStorage.removeItem("fitbit_code_verifier");
      localStorage.removeItem("fitbit_study_code");
      localStorage.removeItem("fitbit_participant_data");

      // Send success message to parent window if in popup
      if (window.opener) {
        window.opener.postMessage(
          {
            type: "FITBIT_OAUTH_SUCCESS",
            fitbitData: {
              participantId: storeData.participantId,
              authorizationStatus: true,
            },
          },
          window.location.origin
        );
        window.close();
        return;
      }

      // If not in popup, redirect to info collection page (step 6 of NewParticipantFlow)
      const navigationData = {
        participantId: storeData.participantId || finalParticipantId,
        studyCode: studyCode,
        email: storedEmail2,
        phoneNumber: storedPhoneNumber2,
        step: 6, // Direct to step 6 (Personal Information)
      };

      // Navigate immediately to information page
      navigate("/participant/info", {
        state: navigationData,
      });
    } catch (err: any) {
      setError(err.message || "Failed to process Fitbit authorization");
      setStatus("error");
      analyticsService.trackParticipantFitbitAuthFailed({ error: err.message || "Authorization failed" });

      // Send error message to parent window if in popup
      if (window.opener) {
        window.opener.postMessage(
          {
            type: "FITBIT_OAUTH_ERROR",
            error: err.message || "Authorization failed",
          },
          window.location.origin
        );
        window.close();
        return;
      }

      // Clean up stored data on error
      localStorage.removeItem("fitbit_code_verifier");
      localStorage.removeItem("fitbit_study_code");
      localStorage.removeItem("fitbit_participant_data");
    } finally {
      // Only set to error if we haven't already set success
      if (!hasSetSuccess.current) {
        setStatus("error");
      }
    }
  }, [searchParams, navigate, hasProcessed]);

  useEffect(() => {
    handleCallback();
  }, [handleCallback]);

  if (status === "loading") {
    return (
      <LoadingSpinner message="Processing Fitbit authorization..." fullScreen />
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
          {status === "success" ? (
            <>
              <CheckCircle
                sx={{ fontSize: 64, color: "success.main", mb: 2 }}
              />
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                {reauthorized ? "Reauthorization complete" : "Fitbit Connected Successfully!"}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {reauthorized
                  ? "New data permissions (SpO2, breathing rate) have been added. You can close this window."
                  : "Your Fitbit device has been connected. Do not close this page. You are being redirected to enter your participant information to finish enrollment."}
              </Typography>
              <Alert severity={reauthorized ? "success" : "warning"} sx={{ mb: 2 }}>
                {reauthorized
                  ? "Reauthorization complete. You can close this window."
                  : "Do not close this page. Redirecting you to complete participant information now."}
              </Alert>
            </>
          ) : (
            <>
              <ErrorIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                Authorization Failed
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                There was an issue connecting your Fitbit device.
              </Typography>
              <Alert severity="error">{error}</Alert>
            </>
          )}

          <Box
            sx={{
              mt: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FitnessCenter sx={{ mr: 1, color: "primary.main" }} />
            <Typography variant="body2" color="text.secondary">
              StudySync Fitbit Integration
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default FitbitCallback;
